import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth, useCart } from "../App";
import { Button } from "../components/ui/button";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { user } = useAuth();
  const { cart, refreshCart, updateGuestCartItem } = useCart();
  const navigate = useNavigate();

  const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
  const items = cart.items || [];

  const updateQty = async (pid, size, q) => {
    if (user) {
      await axios.put(`${API}/cart/items/${pid}`, { quantity: q });
      refreshCart();
    } else {
      await updateGuestCartItem({ product_id: pid, size, quantity: q });
    }
  };
  const remove = async (pid, size) => {
    if (user) {
      await axios.delete(`${API}/cart/items/${pid}`);
      refreshCart();
    } else {
      await updateGuestCartItem({ product_id: pid, size, quantity: 0 });
    }
    toast.success("Removed from cart");
  };

  const shipping = cart.total > 2500 || cart.total === 0 ? 0 : 99;
  const grandTotal = cart.total + shipping;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="cart-page">
      <h1 className="font-display text-4xl text-[#2C2A29] mb-2">Your Cart</h1>
      <p className="text-sm text-[#8A8480] mb-10">{items.length} {items.length === 1 ? "piece" : "pieces"} chosen for you</p>

      {items.length === 0 ? (
        <div className="text-center py-20 bg-white border border-[#EAE3D9] rounded-2xl" data-testid="cart-empty">
          <ShoppingBag className="h-12 w-12 mx-auto text-[#C5A059] mb-4" />
          <p className="font-display text-2xl text-[#2C2A29]">Your cart is empty</p>
          <Link to="/products" className="inline-block mt-6">
            <Button className="btn-primary rounded-full px-8 py-5 h-auto">Begin shopping</Button>
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          <div className="space-y-4" data-testid="cart-items">
            {items.map(it => (
              <div key={`${it.product_id}-${it.size}`} className="bg-white border border-[#EAE3D9] rounded-2xl p-4 flex gap-4" data-testid={`cart-item-${it.product_id}`}>
                <Link to={`/product/${it.product_id}`} className="shrink-0">
                  <img src={it.product?.images?.[0]} alt={it.product?.name} className="h-28 w-28 rounded-xl object-cover bg-[#FFF5F3]" />
                </Link>
                <div className="flex-1">
                  <Link to={`/product/${it.product_id}`} className="font-display text-lg text-[#2C2A29] hover:text-[#8B1E28]">{it.product?.name}</Link>
                  <p className="text-xs text-[#8A8480] mt-1">{it.product?.purity} · {it.product?.weight}g {it.size ? `· Size ${it.size}` : ""}</p>
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex items-center border border-[#EAE3D9] rounded-full">
                      <button onClick={() => updateQty(it.product_id, it.size, it.quantity - 1)} className="h-8 w-8 flex items-center justify-center hover:text-[#8B1E28]" data-testid={`qty-minus-${it.product_id}`}>
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="w-8 text-center text-sm" data-testid={`qty-${it.product_id}`}>{it.quantity}</span>
                      <button onClick={() => updateQty(it.product_id, it.size, it.quantity + 1)} className="h-8 w-8 flex items-center justify-center hover:text-[#8B1E28]" data-testid={`qty-plus-${it.product_id}`}>
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <button onClick={() => remove(it.product_id, it.size)} className="text-[#8A8480] hover:text-[#D32F2F] text-sm flex items-center gap-1" data-testid={`remove-${it.product_id}`}>
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-base font-semibold text-[#2C2A29]">{inr(it.product?.price * it.quantity)}</div>
                  {it.quantity > 1 && <div className="text-xs text-[#8A8480]">{inr(it.product?.price)} each</div>}
                </div>
              </div>
            ))}
          </div>

          <aside className="bg-white border border-[#EAE3D9] rounded-2xl p-6 h-fit sticky top-32" data-testid="cart-summary">
            <h3 className="font-display text-2xl text-[#2C2A29] mb-4">Order summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#595552]">Subtotal</span><span>{inr(cart.total)}</span></div>
              <div className="flex justify-between"><span className="text-[#595552]">Shipping</span><span>{shipping === 0 ? "Free" : inr(shipping)}</span></div>
              <div className="flex justify-between pt-3 border-t border-[#EAE3D9] mt-3">
                <span className="font-semibold">Total</span><span className="font-semibold text-lg" data-testid="cart-total">{inr(grandTotal)}</span>
              </div>
            </div>
            <Button onClick={() => navigate("/checkout")} className="btn-primary w-full mt-6 rounded-full py-6 h-auto" data-testid="checkout-btn">
              Proceed to checkout
            </Button>
            <p className="text-xs text-[#8A8480] mt-4 text-center">Hallmarked · Insured shipping</p>
          </aside>
        </div>
      )}
    </div>
  );
}
